import type { PricingData, Totals, UrgencyMode } from '@rpl/pricing';
import { formatMoney } from '../../lib/format';
import { useCartExtras } from '../../store/cartExtras';
import { useStore } from '../../store/store';
import { keyOfGroup, summaryLabel } from './cartModel';

/**
 * 340px sticky summary (desktop) / stacked block (mobile): per-line list,
 * fulfilment note, Archivo Black total, Checkout CTA, turnaround + trust lines,
 * and the save-for-later affordance under the panel.
 *
 * `urgency` (optional) is the ACTIVE urgent-processing state: 'paid' adds a
 * priced fee row and folds the fee into the Total; 'request' adds an unpriced
 * note (the fee is invoiced after staff confirm, so the Total is unchanged).
 */
export function SummaryPanel({
  totals,
  pricing,
  urgency,
  urgencyUpsell,
  onCheckout,
  onSave,
  checkoutBusy,
  mobile,
}: {
  totals: Totals;
  pricing: PricingData;
  urgency?: { mode: UrgencyMode; fee: number } | null;
  urgencyUpsell?: {
    active: boolean;
    label: string;
    fee: number;
    mode: UrgencyMode;
    onOpen: () => void;
  } | null;
  onCheckout: () => void;
  onSave: () => void;
  checkoutBusy: boolean;
  mobile?: boolean;
}) {
  const savedEmail = useCartExtras((s) => s.savedEmail);
  // Sunk-cost recap (behavioral round): remind the effort already invested.
  const photoCount = useStore((s) => new Set(s.prints.map((p) => p.photoId)).size);
  // Truthful anchor-neutralizer: pickup is free; shipping appears at checkout.
  const pickupNames = ['pickupSydney', 'pickupWollongong']
    .map((k) => pricing.fulfilment[k]?.label?.replace(/^Pickup\s*[—-]\s*/u, ''))
    .filter(Boolean)
    .join(' · ');

  return (
    <div className={`summary${mobile ? ' summary--mobile' : ''}`}>
      <div className="summary__panel">
        <div className="summary__eyebrow">Summary</div>
        {totals.groups.map((g) => (
          <div key={keyOfGroup(g)} className="summary__row">
            <span>{summaryLabel(g)}</span>
            <span>{formatMoney(g.lineTotal)}</span>
          </div>
        ))}
        {urgency?.mode === 'paid' && (
          <div className="summary__row">
            <span>{pricing.urgency?.label ?? 'Urgent processing'}</span>
            <span>+{formatMoney(urgency.fee)}</span>
          </div>
        )}
        <div className="summary__row summary__row--muted">
          <span>Fulfilment</span>
          <span>next step</span>
        </div>
        {urgency?.mode === 'request' && (
          <div className="summary__urgnote">
            Urgency requested. Fee {formatMoney(urgency.fee)} invoiced after we confirm turnaround.
          </div>
        )}
        <div className="summary__total">
          <span className="summary__total-label">Total</span>
          <span className="summary__total-value">
            {formatMoney(totals.subtotal + (urgency?.mode === 'paid' ? urgency.fee : 0))}
          </span>
        </div>
        {totals.printCount > 0 && (
          <div className="summary__recap">
            {totals.printCount} print{totals.printCount === 1 ? '' : 's'} across {photoCount}{' '}
            photo{photoCount === 1 ? '' : 's'} — ready for the lab
          </div>
        )}
        {urgencyUpsell && (
          <button
            type="button"
            className={`btn btn--secondary summary__cta summary__urgency${
              urgencyUpsell.active ? ' summary__urgency--on' : ''
            }`}
            onClick={urgencyUpsell.onOpen}
          >
            ⚡ {urgencyUpsell.label}
            {urgencyUpsell.active
              ? ` · ${urgencyUpsell.mode === 'paid' ? 'ON' : 'REQUESTED'}`
              : ` +${formatMoney(urgencyUpsell.fee)}`}
          </button>
        )}
        {!mobile && (
          <button
            type="button"
            className="btn btn--primary summary__cta"
            onClick={onCheckout}
            disabled={checkoutBusy || totals.groups.length === 0}
          >
            {checkoutBusy ? 'Preparing…' : 'Checkout →'}
          </button>
        )}
        {/* No turnaround promises anywhere in the app (staff round 2026-07-11)
            and no paper name-drops — the C-Type paper line misled fine-art buyers. */}
        {pickupNames && (
          <div className="summary__caption">
            Free pickup from either store ({pickupNames}). Prefer delivery? Shipping is
            calculated at checkout.
          </div>
        )}
        <div className="summary__trust">Ilford Certified Printer Partner</div>
      </div>

      <div className="summary__save">
        {savedEmail ? (
          <>
            <span className="summary__save-note">
              Saved to <strong>{savedEmail}</strong>
            </span>
            <button type="button" className="linkbtn linkbtn--dark" onClick={onSave}>
              Resend or update
            </button>
          </>
        ) : (
          <>
            <span className="summary__save-note">Not ready to order?</span>
            <button type="button" className="linkbtn linkbtn--dark" onClick={onSave}>
              Save my order for later
            </button>
          </>
        )}
      </div>
    </div>
  );
}
